import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useUndoRedo } from '../../../../hooks';
import {
  selectActiveAdvancedEdit,
  selectIsEditFrozen,
  selectRecording,
} from '../../../../selector';
import { resolveDraftCutsWithFallback } from '../../../../utils';
import { AdvancedTimelineOverlays, AdvancedTimelineTrack } from './components';
import { TIMELINE_CONFIG } from './constants';
import {
  TIMELINE_ZOOM_SCALES,
  useEditViewPlayer,
} from './edit-view-player-context';
import {
  useAdvancedTimelineKeyboard,
  useAdvancedTimelineLayout,
  useTimelineDrag,
} from './hooks';
import {
  deleteRangeFromCuts,
  restoreCutFromCuts,
  trimCutEnd,
  trimCutStart,
} from './utils/cut-operations';
import {
  type EditedSegment,
  computeJointsFromSegments,
  cutsToSegments,
} from './utils/cuts-to-segments';
import {
  clamp,
  formatTimeWithMs,
  timeToPixel,
} from './utils/time-calculations';

const MIN_SEGMENT_DURATION_MS = TIMELINE_CONFIG.minSegmentDuration * 1000;

type AdvancedTimelineViewProps = {
  onSplitAtPlayhead: () => void;
  selectedSegmentId: string | null;
  selectedJointId: string | null;
  onSelectSegment: (id: string | null) => void;
  onSelectJoint: (id: string | null) => void;
};

export const AdvancedTimelineView = ({
  onSplitAtPlayhead,
  selectedSegmentId,
  selectedJointId,
  onSelectSegment,
  onSelectJoint,
}: AdvancedTimelineViewProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const isFrozen = useAppSelector(selectIsEditFrozen);
  const {
    outputTime,
    seek,
    pause,
    previewSegmentTime,
    zoomScale,
    setZoomScale,
  } = useEditViewPlayer();
  const { undo, redo } = useUndoRedo();
  const zoom = zoomScale;
  const zoomIndex = TIMELINE_ZOOM_SCALES.findIndex(
    scale => scale === zoomScale,
  );
  const canZoomIn = zoomIndex < TIMELINE_ZOOM_SCALES.length - 1;
  const canZoomOut = zoomIndex > 0;
  const zoomIn = useCallback(() => {
    if (!canZoomIn) {
      return;
    }
    setZoomScale(TIMELINE_ZOOM_SCALES[zoomIndex + 1]);
  }, [canZoomIn, setZoomScale, zoomIndex]);
  const zoomOut = useCallback(() => {
    if (!canZoomOut) {
      return;
    }
    setZoomScale(TIMELINE_ZOOM_SCALES[zoomIndex - 1]);
  }, [canZoomOut, setZoomScale, zoomIndex]);
  const isPlayheadDraggingRef = useRef(false);
  const pendingPlayheadClientXRef = useRef<number | null>(null);
  const playheadDragRafRef = useRef<number | null>(null);

  // canEdit gates all cut/trim/restore interactions on the timeline.
  // Frozen when the edit is PROCESSING (export in-flight) or COMPLETED.
  const canEdit = Boolean(activeEdit) && !isFrozen;
  const recordingDurationSec = recording?.duration ?? 0;
  const cuts = useMemo(
    () =>
      resolveDraftCutsWithFallback({
        draftCuts: activeEdit?.draft?.cuts ?? [],
        recordingId: recording?.id,
        recordingDurationSec,
      }),
    [activeEdit?.draft?.cuts, recording?.id, recordingDurationSec],
  );
  const segments = useMemo(
    () =>
      cutsToSegments(cuts, recording?.id ?? 'recording', recordingDurationSec),
    [cuts, recording?.id, recordingDurationSec],
  );
  const joints = useMemo(
    () => computeJointsFromSegments(segments, recording?.id ?? 'recording'),
    [segments, recording?.id],
  );
  const activeSegments = useMemo(
    () => segments.filter(segment => !segment.isDeleted),
    [segments],
  );
  const interactiveJoints = useMemo(
    () =>
      joints.filter(
        joint => joint.beforeSegmentId != null && joint.afterSegmentId != null,
      ),
    [joints],
  );

  const outputDurationSec = useMemo(() => {
    const activeSegments = segments.filter(segment => !segment.isDeleted);
    if (activeSegments.length === 0) {
      return 0;
    }
    return activeSegments[activeSegments.length - 1].outputEndTime / 1000;
  }, [segments]);
  const {
    timelineViewportRef,
    pixelsPerSecond,
    timelineWidthPx,
    playbackPlayheadPositionPx,
  } = useAdvancedTimelineLayout({
    outputDurationSec,
    zoom,
    outputTimeSec: outputTime,
  });

  const seekAndScrollToOutputTime = useCallback(
    (targetOutputTimeSec: number) => {
      const boundedOutputTimeSec = clamp(
        targetOutputTimeSec,
        0,
        Math.max(0, outputDurationSec),
      );
      seek(boundedOutputTimeSec);

      const viewport = timelineViewportRef.current;
      if (!viewport) {
        return;
      }

      const playheadX = timeToPixel(
        boundedOutputTimeSec,
        pixelsPerSecond,
        zoom,
      );
      const viewportLeft = viewport.scrollLeft;
      const viewportRight = viewportLeft + viewport.clientWidth;
      if (playheadX < viewportLeft || playheadX > viewportRight) {
        const nextScrollLeft = Math.max(
          0,
          playheadX - viewport.clientWidth / 2,
        );
        if (typeof viewport.scrollTo === 'function') {
          viewport.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        } else {
          viewport.scrollLeft = nextScrollLeft;
        }
      }
    },
    [outputDurationSec, pixelsPerSecond, seek, timelineViewportRef, zoom],
  );

  const seekToClientX = useCallback(
    (clientX: number) => {
      const viewport = timelineViewportRef.current;
      if (!viewport) {
        return;
      }

      const rect = viewport.getBoundingClientRect();
      const pixelOffset = clientX - rect.left + viewport.scrollLeft;
      const nextOutputTimeSec = clamp(
        pixelOffset / Math.max(1, pixelsPerSecond * zoom),
        0,
        outputDurationSec,
      );
      seek(nextOutputTimeSec);
    },
    [outputDurationSec, pixelsPerSecond, seek, timelineViewportRef, zoom],
  );

  const flushPlayheadDrag = useCallback(() => {
    playheadDragRafRef.current = null;
    const clientX = pendingPlayheadClientXRef.current;
    if (clientX == null) {
      return;
    }
    seekToClientX(clientX);
  }, [seekToClientX]);

  const schedulePlayheadSeek = useCallback(
    (clientX: number) => {
      pendingPlayheadClientXRef.current = clientX;
      if (playheadDragRafRef.current != null) {
        return;
      }
      playheadDragRafRef.current =
        window.requestAnimationFrame(flushPlayheadDrag);
    },
    [flushPlayheadDrag],
  );

  const handlePlaybackPlayheadMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      pause();
      isPlayheadDraggingRef.current = true;
      schedulePlayheadSeek(event.clientX);
    },
    [pause, schedulePlayheadSeek],
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isPlayheadDraggingRef.current) {
        return;
      }
      schedulePlayheadSeek(event.clientX);
    };

    const handleMouseUp = () => {
      if (!isPlayheadDraggingRef.current) {
        return;
      }
      const pendingClientX = pendingPlayheadClientXRef.current;
      if (playheadDragRafRef.current != null) {
        window.cancelAnimationFrame(playheadDragRafRef.current);
        playheadDragRafRef.current = null;
      }
      if (pendingClientX != null) {
        seekToClientX(pendingClientX);
      }
      pendingPlayheadClientXRef.current = null;
      isPlayheadDraggingRef.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (playheadDragRafRef.current != null) {
        window.cancelAnimationFrame(playheadDragRafRef.current);
        playheadDragRafRef.current = null;
      }
    };
  }, [schedulePlayheadSeek, seekToClientX]);

  const dispatchDraftUpdate = useCallback(
    (nextCuts: typeof cuts) => {
      if (!activeEdit) {
        return;
      }
      dispatch({
        type: '[advanced-editor] edit draft changed',
        payload: {
          editId: activeEdit.id,
          draft: { ...activeEdit.draft, cuts: nextCuts },
          previousDraft: activeEdit.draft,
        },
      });
    },
    [activeEdit, dispatch],
  );

  const dispatchDeleteSegment = useCallback(
    (segmentId: string) => {
      if (!canEdit) return;
      // Must always keep at least one segment — deletion is a no-op when only one remains.
      if (activeSegments.length <= 1) return;
      const targetSegment = activeSegments.find(s => s.id === segmentId);
      if (!targetSegment) return;

      const targetIndex = activeSegments.findIndex(s => s.id === segmentId);
      const nextSegment =
        targetIndex >= 0 ? activeSegments[targetIndex + 1] : null;
      const previousSegment =
        targetIndex >= 0 ? activeSegments[targetIndex - 1] : null;
      const nextOutputTimeSec = nextSegment
        ? targetSegment.outputStartTime / 1000
        : previousSegment
        ? previousSegment.outputEndTime / 1000
        : 0;

      dispatchDraftUpdate(
        deleteRangeFromCuts(
          cuts,
          targetSegment.originalStartTime,
          targetSegment.originalEndTime,
        ),
      );
      seekAndScrollToOutputTime(nextOutputTimeSec);
    },
    [
      activeSegments,
      canEdit,
      cuts,
      dispatchDraftUpdate,
      seekAndScrollToOutputTime,
    ],
  );

  const dispatchDeleteSelectedSegment = useCallback(() => {
    if (selectedSegmentId) dispatchDeleteSegment(selectedSegmentId);
  }, [dispatchDeleteSegment, selectedSegmentId]);

  const handleRemoveCutAtJoint = useCallback(
    (jointId: string) => {
      if (!canEdit) {
        return;
      }

      const targetJoint = interactiveJoints.find(joint => joint.id === jointId);
      if (!targetJoint) {
        return;
      }

      let nextCuts = cuts;
      if (targetJoint.deletedDuration > 0) {
        if (targetJoint.deletedSegments.length === 0) {
          return;
        }
        const gapStartMs = targetJoint.deletedSegments[0].originalStartTime;
        const gapEndMs =
          targetJoint.deletedSegments[targetJoint.deletedSegments.length - 1]
            .originalEndTime;
        nextCuts = restoreCutFromCuts(cuts, gapStartMs, gapEndMs);
      } else {
        if (
          targetJoint.beforeSegmentId == null ||
          targetJoint.afterSegmentId == null
        ) {
          return;
        }
        const beforeSegment = activeSegments.find(
          segment => segment.id === targetJoint.beforeSegmentId,
        );
        const afterSegment = activeSegments.find(
          segment => segment.id === targetJoint.afterSegmentId,
        );
        if (!beforeSegment || !afterSegment) {
          return;
        }
        nextCuts = restoreCutFromCuts(
          cuts,
          beforeSegment.originalEndTime,
          afterSegment.originalStartTime,
        );
      }

      dispatchDraftUpdate(nextCuts);
      onSelectJoint(null);
    },
    [
      activeSegments,
      canEdit,
      cuts,
      dispatchDraftUpdate,
      interactiveJoints,
      onSelectJoint,
    ],
  );

  const { draggingSegmentId, draggingHandle, previewTime, startDrag } =
    useTimelineDrag(
      zoom,
      {
        onTrimStart: (segmentId, newStartMs) => {
          dispatchDraftUpdate(trimCutStart(cuts, segmentId, newStartMs));
        },
        onTrimEnd: (segmentId, newEndMs) => {
          dispatchDraftUpdate(trimCutEnd(cuts, segmentId, newEndMs));
        },
      },
      pixelsPerSecond,
      timelineViewportRef,
    );

  useAdvancedTimelineKeyboard({
    canEdit,
    onEscape: () => {
      onSelectJoint(null);
      onSelectSegment(null);
    },
    onSplit: onSplitAtPlayhead,
    onUndo: undo,
    onRedo: redo,
    onDelete: () => {
      if (selectedJointId) {
        handleRemoveCutAtJoint(selectedJointId);
      } else {
        dispatchDeleteSelectedSegment();
      }
    },
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
  });

  const draggingSegment = activeSegments.find(
    segment => segment.id === draggingSegmentId,
  );

  useEffect(() => {
    if (!draggingSegment || previewTime == null) {
      return;
    }

    previewSegmentTime(draggingSegment.id, previewTime / 1000);
  }, [draggingSegment, previewSegmentTime, previewTime]);

  const trimPreviewPositionPx =
    draggingSegment && draggingHandle && previewTime != null
      ? clamp(
          timeToPixel(
            (draggingSegment.outputStartTime +
              (previewTime - draggingSegment.originalStartTime)) /
              1000,
            pixelsPerSecond,
            zoom,
          ),
          0,
          Math.max(0, timelineWidthPx - 1),
        )
      : null;
  const isTrimPreviewNearLeft =
    trimPreviewPositionPx != null && trimPreviewPositionPx < 36;
  const isTrimPreviewNearRight =
    trimPreviewPositionPx != null &&
    trimPreviewPositionPx > Math.max(0, timelineWidthPx - 36);
  useEffect(() => {
    if (
      selectedJointId &&
      !interactiveJoints.some(joint => joint.id === selectedJointId)
    ) {
      onSelectJoint(null);
    }
  }, [interactiveJoints, onSelectJoint, selectedJointId]);

  const handleSegmentClick = useCallback(
    (segment: EditedSegment, clientX: number) => {
      if (clientX >= 0) {
        seekToClientX(clientX);
      } else {
        // Keyboard activation — no clientX available, fall back to segment start
        const outputMs = clamp(
          segment.outputStartTime,
          0,
          segment.outputEndTime,
        );
        seek(outputMs / 1000);
      }
      pause();
    },
    [pause, seek, seekToClientX],
  );

  const handleTrimHandleMouseDown = useCallback(
    (
      segment: EditedSegment,
      handle: 'start' | 'end',
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      const minTimeMs =
        handle === 'start'
          ? 0
          : segment.originalStartTime + MIN_SEGMENT_DURATION_MS;
      const maxTimeMs =
        handle === 'start'
          ? segment.originalEndTime - MIN_SEGMENT_DURATION_MS
          : Math.max(
              recordingDurationSec * 1000,
              segment.originalStartTime + MIN_SEGMENT_DURATION_MS,
            );
      startDrag({
        segmentId: segment.id,
        handle,
        initialClientX: event.clientX,
        initialTimeMs:
          handle === 'start'
            ? segment.originalStartTime
            : segment.originalEndTime,
        minTimeMs,
        maxTimeMs,
      });
    },
    [recordingDurationSec, startDrag],
  );

  if (!recording) {
    return (
      <div className="h-full w-full" data-testid="advanced-timeline-empty">
        {t('content-recording.editor-v2.advanced-timeline-unavailable')}
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col gap-3 bg-spring-neutral-base px-12"
      data-testid="advanced-timeline-view"
    >
      <AdvancedTimelineTrack
        canEdit={canEdit}
        isFrozen={isFrozen}
        layout={{
          timelineViewportRef,
          timelineWidthPx,
          outputDurationSec,
          zoom,
          pixelsPerSecond,
        }}
        segments={{ activeSegments, interactiveJoints }}
        selection={{ selectedSegmentId, selectedJointId }}
        drag={{ draggingSegmentId, draggingHandle, previewTime }}
        handlers={{
          onSeek: seek,
          onSelectSegment: segmentId => {
            onSelectSegment(segmentId);
            onSelectJoint(null);
          },
          onSelectJoint: jointId => {
            onSelectJoint(jointId);
            onSelectSegment(null);
          },
          onRemoveCut: handleRemoveCutAtJoint,
          onDeleteSegment: dispatchDeleteSegment,
          onSegmentClick: handleSegmentClick,
          onTrimHandleMouseDown: handleTrimHandleMouseDown,
        }}
      >
        <AdvancedTimelineOverlays
          outputTimeSec={outputTime}
          playbackPlayheadPositionPx={playbackPlayheadPositionPx}
          trimPreviewPositionPx={trimPreviewPositionPx}
          trimPreviewTimeMs={previewTime}
          isTrimPreviewNearLeft={isTrimPreviewNearLeft}
          isTrimPreviewNearRight={isTrimPreviewNearRight}
          rulerHeightPx={TIMELINE_CONFIG.rulerHeight}
          trackHeightPx={TIMELINE_CONFIG.trackHeight}
          formatTimeWithMs={formatTimeWithMs}
          onPlaybackPlayheadMouseDown={handlePlaybackPlayheadMouseDown}
        />
      </AdvancedTimelineTrack>
    </div>
  );
};
